/**
 * Notification Service
 * Handles in-app notifications and email notifications via Cloud Functions
 */

import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
} from "firebase/firestore";
import { getFirebaseDB } from "@/lib/firebase";
import { Notification, NotificationType } from "@/types/types";

/**
 * Create a new notification
 */
export async function createNotification(
  notificationData: Omit<Notification, "id" | "createdAt" | "read" | "readAt">
): Promise<string> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const notificationsRef = collection(db, "notifications");
  const newNotificationRef = doc(notificationsRef);

  const notification: Notification = {
    ...notificationData,
    id: newNotificationRef.id,
    read: false,
    createdAt: new Date().toISOString(),
  };

  await setDoc(newNotificationRef, {
    ...notification,
    createdAt: serverTimestamp(),
  });

  return newNotificationRef.id;
}

/**
 * Get user's notifications
 */
export async function getUserNotifications(
  userId: string,
  limitCount: number = 50,
  unreadOnly: boolean = false
): Promise<Notification[]> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const notificationsRef = collection(db, "notifications");
  let q = query(
    notificationsRef,
    where("userId", "==", userId),
    orderBy("createdAt", "desc"),
    limit(limitCount)
  );

  if (unreadOnly) {
    q = query(
      notificationsRef,
      where("userId", "==", userId),
      where("read", "==", false),
      orderBy("createdAt", "desc"),
      limit(limitCount)
    );
  }

  const querySnapshot = await getDocs(q);
  const notifications: Notification[] = [];

  querySnapshot.forEach((doc) => {
    const data = doc.data();
    notifications.push({
      ...data,
      id: doc.id,
      createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
      readAt: data.readAt?.toDate?.()?.toISOString() || data.readAt,
    } as Notification);
  });

  return notifications;
}

/**
 * Subscribe to user's notifications (real-time)
 */
export function subscribeToNotifications(
  userId: string,
  callback: (notifications: Notification[]) => void
): Unsubscribe {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const notificationsRef = collection(db, "notifications");
  const q = query(
    notificationsRef,
    where("userId", "==", userId),
    orderBy("createdAt", "desc"),
    limit(50)
  );

  return onSnapshot(q, (snapshot) => {
    const notifications: Notification[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      notifications.push({
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
        readAt: data.readAt?.toDate?.()?.toISOString() || data.readAt,
      } as Notification);
    });
    callback(notifications);
  });
}

/**
 * Mark notification as read
 */
export async function markNotificationAsRead(
  notificationId: string
): Promise<boolean> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const notificationRef = doc(db, "notifications", notificationId);
  await updateDoc(notificationRef, {
    read: true,
    readAt: serverTimestamp(),
  });

  return true;
}

/**
 * Mark all notifications as read for user
 */
export async function markAllNotificationsAsRead(
  userId: string
): Promise<boolean> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const notifications = await getUserNotifications(userId, 100, true);

  const updatePromises = notifications.map((notification) =>
    markNotificationAsRead(notification.id)
  );

  await Promise.all(updatePromises);
  return true;
}

/**
 * Get unread notification count
 */
export async function getUnreadNotificationCount(
  userId: string
): Promise<number> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const notificationsRef = collection(db, "notifications");
  const q = query(
    notificationsRef,
    where("userId", "==", userId),
    where("read", "==", false)
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.size;
}

/**
 * Helper: Create application notification
 */
export async function notifyApplicationStatusChange(
  tenantId: string,
  applicationId: string,
  status: "approved" | "rejected",
  propertyTitle: string
): Promise<string> {
  const notificationType: NotificationType =
    status === "approved" ? "application_approved" : "application_rejected";

  return createNotification({
    userId: tenantId,
    type: notificationType,
    title:
      status === "approved" ? "Application Approved!" : "Application Update",
    message:
      status === "approved"
        ? `Your application for ${propertyTitle} has been approved. The landlord will contact you to finalize the lease.`
        : `Your application for ${propertyTitle} was not approved at this time.`,
    actionUrl: `/tenant/applications/${applicationId}`,
    actionLabel: "View Application",
    metadata: {
      applicationId,
    },
  });
}

/**
 * Helper: Create lease notification
 */
export async function notifyLeaseCreated(
  tenantId: string,
  leaseId: string,
  propertyTitle: string
): Promise<string> {
  return createNotification({
    userId: tenantId,
    type: "lease_created",
    title: "New Lease Agreement",
    message: `A lease agreement has been created for ${propertyTitle}. Please review and sign.`,
    actionUrl: `/tenant/dashboard?tab=lease`,
    actionLabel: "Review Lease",
    metadata: {
      leaseId,
    },
  });
}

/**
 * Helper: Notify user that lease has been fully signed
 */
export async function notifyLeaseSigned(
  userId: string,
  leaseId: string,
  userRole: "landlord" | "tenant" = "landlord"
): Promise<string> {
  const actionUrl =
    userRole === "tenant"
      ? `/tenant/dashboard?tab=lease`
      : `/landlord/leases/${leaseId}`;

  return createNotification({
    userId,
    type: "lease_created",
    title: "Lease Agreement Active",
    message:
      "The lease agreement has been signed by all parties and is now active.",
    actionUrl,
    actionLabel: "View Lease",
    metadata: {
      leaseId,
    },
  });
}

/**
 * Helper: Create payment notification
 */
export async function notifyPaymentDue(
  tenantId: string,
  paymentId: string,
  amount: number,
  dueDate: string
): Promise<string> {
  return createNotification({
    userId: tenantId,
    type: "payment_due",
    title: "Rent Payment Due",
    message: `Your rent payment of ₦${amount.toLocaleString()} is due on ${new Date(
      dueDate
    ).toLocaleDateString()}.`,
    actionUrl: `/tenant/dashboard?tab=payments`,
    actionLabel: "Pay Now",
    metadata: {
      paymentId,
      amount,
    },
  });
}

/**
 * Helper: Create property approval notification
 */
export async function notifyPropertyApproval(
  landlordId: string,
  propertyId: string,
  propertyTitle: string,
  approved: boolean,
  reason?: string
): Promise<string> {
  return createNotification({
    userId: landlordId,
    type: approved ? "property_approved" : "property_rejected",
    title: approved ? "Property Approved!" : "Property Needs Review",
    message: approved
      ? `Your property "${propertyTitle}" has been approved and is now visible on the marketplace.`
      : `Your property "${propertyTitle}" requires changes before approval. ${
          reason || ""
        }`,
    actionUrl: `/landlord/properties/${propertyId}`,
    actionLabel: approved ? "View Property" : "Edit Property",
    metadata: {
      propertyId,
      reason,
    },
  });
}

/**
 * Helper: Create maintenance request notification for landlord
 */
export async function notifyMaintenanceRequest(
  landlordId: string,
  requestId: string,
  propertyTitle: string,
  category: string
): Promise<string> {
  return createNotification({
    userId: landlordId,
    type: "maintenance_requested",
    title: "New Maintenance Request",
    message: `A ${category} maintenance request has been submitted for ${propertyTitle}.`,
    actionUrl: `/landlord/maintenance`,
    actionLabel: "View Request",
    metadata: {
      requestId,
    },
  });
}

/**
 * Helper: Notify tenant of maintenance status update
 */
export async function notifyMaintenanceUpdate(
  tenantId: string,
  requestId: string,
  status: string,
  category: string
): Promise<string> {
  const statusMessages: Record<string, string> = {
    scheduled: "has been scheduled",
    "in-progress": "is now in progress",
    completed: "has been completed",
    cancelled: "has been cancelled",
  };

  const message = statusMessages[status]
    ? `Your ${category} maintenance request ${statusMessages[status]}.`
    : `Your ${category} maintenance request status has been updated to: ${status}.`;

  return createNotification({
    userId: tenantId,
    type: "maintenance_requested",
    title: "Maintenance Request Updated",
    message,
    actionUrl: `/tenant/dashboard?tab=maintenance`,
    actionLabel: "View Details",
    metadata: {
      requestId,
      status,
    },
  });
}

/**
 * Helper: Create message notification
 */
export async function notifyNewMessage(
  recipientId: string,
  senderName: string,
  conversationId: string,
  preview: string
): Promise<string> {
  return createNotification({
    userId: recipientId,
    type: "message_received",
    title: `New message from ${senderName}`,
    message: preview.substring(0, 100),
    actionUrl: `/messages/${conversationId}`,
    actionLabel: "Reply",
    metadata: {
      conversationId,
    },
  });
}
